import { useState } from 'react';
import { Download, Loader2, ChevronDown, AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FormatType, formats, getFormatDimensions, getFormatLabel as getFormatLabelUtil } from './FormatSelector';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

export type FailedFormatReason = 'overloaded' | 'timeout' | 'unknown';

export interface FailedFormat {
  format: FormatType;
  reason: FailedFormatReason;
}

interface GeneratedImage {
  format: FormatType;
  url: string;
}

interface OutputPanelProps {
  images: GeneratedImage[];
  isLoading: boolean;
  loadingFormats: FormatType[];
  hideTitle?: boolean;
  sourceFileName?: string;
  failedFormats?: FailedFormat[];
  onRetryFormat?: (format: FormatType) => void;
}

export function OutputPanel({ images, isLoading, loadingFormats, hideTitle, sourceFileName, failedFormats = [], onRetryFormat }: OutputPanelProps) {
  const { t } = useLanguage();
  const [collapsedImages, setCollapsedImages] = useState<Set<string>>(new Set());
  const isMultipleImages = images.length > 1;

  const isExpanded = (key: string) => !collapsedImages.has(key);

  const handleToggle = (key: string) => {
    setCollapsedImages((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const getDisplayInfo = (formatId: FormatType) => {
    const preset = formats.find(f => f.id === formatId);
    if (preset) {
      return { label: getFormatLabelUtil(formatId, t), dimensions: preset.dimensions, aspectRatio: preset.aspectRatio };
    }
    const dims = getFormatDimensions(formatId);
    if (dims) {
      return { label: `${dims.width}×${dims.height}`, dimensions: `${dims.width}×${dims.height}`, aspectRatio: `${dims.width} / ${dims.height}` };
    }
    return { label: formatId, dimensions: '', aspectRatio: '1 / 1' };
  };

  const handleDownload = async (url: string, format: FormatType) => {
    const info = getDisplayInfo(format);
    const baseName = sourceFileName
      ? sourceFileName.replace(/\.[^/.]+$/, '').slice(0, 50)
      : null;
    const prefix = baseName && baseName.length <= 50
      ? baseName
      : `scalemage-${Date.now()}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = `${prefix}-${format}-${info.dimensions || ''}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderImageCard = (image: GeneratedImage, index: number) => {
    const info = getDisplayInfo(image.format);
    const key = `${image.format}-${index}`;

    const header = (
      <div className={cn(
        "p-3 border-b border-border flex items-center justify-between",
        isMultipleImages && "cursor-pointer hover:bg-secondary/50 transition-colors"
      )}>
        <div className="flex items-center gap-2">
          {isMultipleImages && (
            <ChevronDown 
              className={cn(
                "h-4 w-4 transition-transform duration-200",
                !isExpanded(key) && "-rotate-90"
              )} 
            />
          )}
          <span className="font-medium">{info.label}</span>
          {info.dimensions && info.label !== info.dimensions && (
            <span className="text-xs text-muted-foreground">{info.dimensions}</span>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            handleDownload(image.url, image.format);
          }}
        >
          <Download className="h-4 w-4 mr-2" />
          {t('outputPanel', 'download')}
        </Button>
      </div>
    );

    const imageContent = (
      <div className="p-2">
        <div
          className="w-full overflow-hidden rounded-lg"
          style={{ aspectRatio: info.aspectRatio.replace(':', ' / ') }}
        >
          <img
            src={image.url}
            alt={`${t('outputPanel', 'generated')} ${info.label}`}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
      </div>
    );

    if (!isMultipleImages) {
      return (
        <div key={key} className="bg-secondary/30 rounded-xl overflow-hidden">
          {header}
          {imageContent}
        </div>
      );
    }

    return (
      <Collapsible
        key={key}
        open={isExpanded(key)}
        onOpenChange={() => handleToggle(key)}
        className="bg-secondary/30 rounded-xl overflow-hidden"
      >
        <CollapsibleTrigger asChild>{header}</CollapsibleTrigger>
        <CollapsibleContent>{imageContent}</CollapsibleContent>
      </Collapsible>
    );
  };

  const getErrorMessage = (reason: FailedFormatReason): string => {
    switch (reason) {
      case 'overloaded': return t('outputPanel', 'errorModelOverloaded');
      case 'timeout': return t('outputPanel', 'errorTimedOut');
      default: return t('outputPanel', 'errorUnknown');
    }
  };

  const renderErrorCard = (failed: FailedFormat) => {
    const info = getDisplayInfo(failed.format);
    return (
      <div key={`error-${failed.format}`} className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">{info.label}</p>
            <p className="text-sm text-muted-foreground mt-1">{getErrorMessage(failed.reason)}</p>
            <p className="text-xs text-muted-foreground mt-1">{t('outputPanel', 'errorActionHint')}</p>
          </div>
          {onRetryFormat && (
            <Button size="sm" variant="outline" className="flex-shrink-0" onClick={() => onRetryFormat(failed.format)}>
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              {t('outputPanel', 'retryFormat')}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {!hideTitle && <h2 className="text-lg font-semibold mb-4">{t('outputPanel', 'output')}</h2>}
      
      {images.length === 0 && !isLoading && failedFormats.length === 0 ? (
        <div className="flex-1 flex items-center justify-center border-2 border-dashed border-border rounded-xl bg-secondary/20">
          <p className="text-muted-foreground text-center">{t('outputPanel', 'generatedImagesWillAppear')}</p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {loadingFormats.map((format) => {
            const info = getDisplayInfo(format);
            return (
              <div key={`loading-${format}`} className="bg-secondary/30 rounded-xl p-4 flex items-center justify-center aspect-video">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">
                    {t('outputPanel', 'generating')} {info.label}...
                  </p>
                </div>
              </div>
            );
          })}
          {failedFormats.map((failed) => renderErrorCard(failed))}
          {images.map((image, index) => renderImageCard(image, index))}
        </div>
      )}
    </div>
  );
}
